import React, { useMemo, useState, useEffect } from "react";
import { Document } from "../types/Document";
import "../styles/DocumentListeTechnicienPage.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Link } from "react-router-dom";
import AuthentifiedPage from "./AuthentifiedPage";
import { GroupeImportService } from "../services/GroupeImportService";
import { useAuth } from "../context/AuthContext";
import { DocumentStatus, StatusBadge } from "../constants/DocStatus";
import DynamicTable from "../components/DynamicTable";

const DocumentListeTechnicienPage: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [query, setQuery] = useState("");
  const [statutFilter, setStatutFilter] = useState<DocumentStatus>(DocumentStatus.TOUT);
  const [domaineFilter, setDomaineFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState<any[]>([]);
  const {api} = useAuth();

  useEffect(() => {
    GroupeImportService.getAll(api).then((value)=>{
      const docs:any[]=[];

      value.forEach((groupImport)=>{
        const nomFenetre = groupImport.fenetre.nomFenetre;
        const nomSousFenetre = groupImport.sousFenetre?.nomSousFenetre;

        docs.push({
          id: groupImport.idGroupeImport,
          domaine: groupImport.fenetre.menu.nomMenu,
          contenu: nomSousFenetre
            ? `${nomFenetre} - ${nomSousFenetre}`
            : nomFenetre,
          date: new Date(groupImport.dateImport).toLocaleDateString(),
          statut: groupImport.status as DocumentStatus
        });
      })

      setDocuments(docs);
    });
  }, []);

  const handleValider = async () => {
    if (popupData.length === 0) return;
    const idGroupeImport = popupData[0].id_groupe_import;

    try {
      await GroupeImportService.validerGroupeImport(api, idGroupeImport);

      // Mise à jour du front
      setDocuments((prevDocs) =>
        prevDocs.map((d) =>
          d.id === idGroupeImport ? { ...d, statut: DocumentStatus.VALIDE } : d
        )
      );

      // Mise à jour popup si nécessaire
      setPopupData((prev) =>
        prev.map((item) => ({ ...item, status: DocumentStatus.VALIDE }))
      );

      setShowPopup(false);
    } catch (error) {
      console.error("Erreur lors de la validation :", error);
    }
  };

  const handleRejeter = async () => {
    if (popupData.length === 0) return;
    const idGroupeImport = popupData[0].id_groupe_import;

    try {
      await GroupeImportService.rejeterGroupeImport(api, idGroupeImport);

      // Mise à jour du front
      setDocuments((prevDocs) =>
        prevDocs.map((d) =>
          d.id === idGroupeImport ? { ...d, statut: DocumentStatus.REJETE } : d
        )
      );

      // Mise à jour popup si nécessaire
      setPopupData((prev) =>
        prev.map((item) => ({ ...item, status: DocumentStatus.REJETE }))
      );

      setShowPopup(false);
    } catch (error) {
      console.error("Erreur lors du rejet :", error);
    }
  };

  const filtered = useMemo(() => {
    let list = documents.slice();
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(
        (d) => d.contenu.toLowerCase().includes(q) || d.domaine.toLowerCase().includes(q)
      );
    }
    if (statutFilter && statutFilter!=DocumentStatus.TOUT) list = list.filter((d) => d.statut === statutFilter);
    if (domaineFilter) list = list.filter((d) => d.domaine === domaineFilter);
    return list;
  }, [query, statutFilter, domaineFilter, documents]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageData = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const totalValide = documents.filter((d) => d.statut === DocumentStatus.VALIDE).length;
  const totalAttente = documents.filter((d) => d.statut === DocumentStatus.EN_ATTENTE).length;
  const totalRetour = documents.filter((d) => d.statut === DocumentStatus.REJETE).length;

  const domaines = Array.from(new Set(documents.map((d) => d.domaine)));
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <AuthentifiedPage>
      <main className="container py-4">
        {/* Header avec compteurs */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <h4 className="tech-doc-title mb-0">Liste des documents</h4>
          <div className="tech-doc-counts d-flex flex-wrap gap-3">
            <span className="total">
              Nombre de documents : <strong>{total}</strong>
            </span>
            <span className="valide">
              Validés : <strong>{totalValide}</strong>{" "}
            </span>
            <span className="retour">
              Retours : <strong>{totalRetour}</strong>
            </span>
          </div>
        </div>

        {/* Barre de recherche et filtres */}
        <div className="tech-filters-bar">
          <div className="input-wrapper">
            <span className="search-icon">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher un agent / contenu"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <select
            className="custom-select"
            value={statutFilter}
            onChange={(e) => setStatutFilter(Number(e.target.value) as DocumentStatus)}
          >
            <option value="-1">Tous les statuts</option>
            <option value="0">En attente</option>
            <option value="1">Validé</option>
            <option value="2">Retour à corriger</option>
          </select>

          <select
            className="custom-select"
            value={domaineFilter}
            onChange={(e) => setDomaineFilter(e.target.value)}
          >
            <option value="">Tous les domaines</option>
            {domaines.map((dom) => (
              <option key={dom} value={dom}>
                {dom}
              </option>
            ))}
          </select>

          <select className="custom-select">
            <option value="">Trier par date</option>
            <option value="recent">Le plus récent</option>
            <option value="ancien">Le plus ancien</option>
          </select>
        </div>

        {/* Tableau */}
        <div className="table-responsive shadow-sm">
          <table className="table table-hover align-middle">
            <thead className="table-success">
              <tr>
                <th>Domaine</th>
                <th>Contenu</th>
                <th>Date de soumission</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageData.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <div className="fw-semibold">{doc.domaine}</div>
                    <div className="text-muted small mb-1">{doc.description}</div>
                  </td>
                  <td>{doc.contenu}</td>
                  <td>{doc.date}</td>
                  <td>
                    <StatusBadge statut={doc.statut} />
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-sm btn-outline-success"
                      title="Voir les données"
                      onClick={async () => {
                        try {
                          // Appel backend pour récupérer les données du groupe import
                          const datas = await GroupeImportService.getDatasForGroupImport(api, doc.id);

                          // Met à jour le state pour le popup
                          setPopupData(datas);
                          setShowPopup(true);
                        } catch (error) {
                          console.error(
                            `Erreur lors de la récupération des données du groupe import ${doc.id}:`,
                            error
                          );
                        }
                      }}>
                      <i className="bi bi-pencil-square"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <small className="text-muted">
            {pageData.length} documents affichés sur {total}
          </small>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li key={num} className={`page-item ${num === currentPage ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(num)}>
                    {num}
                  </button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>

        {/* Popup */}
        {showPopup && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            background: "rgba(0,0,0,0.5)",
            zIndex: 1050,
          }}
        >
          <div
            className="position-fixed top-50 start-50 translate-middle"
            style={{
              width: "90vw",
              maxWidth: "1200px",
              maxHeight: "80vh",
              overflowY: "auto",  
              backgroundColor: "white",
              borderRadius: "0.5rem",
              boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.3)",
            }}
          >
            <div className="modal-header">
              <h5 className="modal-title">Données du document</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowPopup(false)}
              ></button>
            </div>

            <div className="modal-body">
              {popupData.length === 0 ? (
                <p>Aucune donnée disponible</p>
              ) : (
                <DynamicTable data={popupData}/>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-success" onClick={handleValider}>
                Valider
              </button>
              <button className="btn btn-danger" onClick={handleRejeter}>
                Rejeter
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowPopup(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      </main>
    </AuthentifiedPage>
  );
};

export default DocumentListeTechnicienPage;
